import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ValueAccessorBase } from '../shared/value-accessor/value-accessor-base';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';

/**
 * Component for entering cadastral municipality data
 * Consists of:
 * - Integer input for Katastrska občina (cadastral municipality code)
 * - Read-only input showing the municipality name fetched from GURS API
 */
@Component({
    selector: 'sem-cadastral-parcel-control',
    templateUrl: './sem-cadastral-parcel-control.component.html',
    styleUrls: ['./sem-cadastral-parcel-control.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: SemCadastralParcelControl, multi: true }
    ],
    standalone: false
})
export class SemCadastralParcelControl extends ValueAccessorBase<string> implements OnInit {
  
  @Input() onValueChangeEval: string = '';
  @Input() bindingPropertyName: string = 'KatastrskaObcinaName'; // For municipality name
  @Input() cadastralMunicipalityNumber: string = 'CadastralMunicipalityNumber'; // For municipality number
  @Input() readOnly: boolean = false;
  @Input() maxLengthKo: number = 4;

  // Component state
  katastrskaObcina: number | null = null;
  katastrskaObcinaName: string = '';
  
  // Loading state
  isLoadingName: boolean = false;
  
  // Parse the dictionary if it comes as a string
  private municipalityDictionary: { [key: number]: string } = {};
  
  // Subject for municipality requests to handle race conditions
  private municipalityRequestSubject = new Subject<number>();


  ngOnInit() {
    super.ngOnInit();
    
    // Set up municipality name fetching with race condition protection
    this.municipalityRequestSubject.pipe(
      debounceTime(300), // Wait 300ms after user stops typing
      distinctUntilChanged(), // Only process if value actually changed
      switchMap(koId => {
        if (!koId) {
          return of({ success: true, name: '', koId: null });
        }
        
        this.isLoadingName = true;
        const url = `/api/gurs/cadastral-municipality/${koId}`;
        console.log('Fetching municipality name for:', koId, 'URL:', url);
        
        return this.restProviderService.getDataGET(url);
      })
    ).subscribe(
      (response: any) => {
        this.isLoadingName = false;
        
        // Parse the response if it's a string
        let parsedResponse;
        try {
          parsedResponse = typeof response === 'string' ? JSON.parse(response) : response;
          console.log('Municipality API response (parsed):', parsedResponse);
        } catch (e) {
          console.error('Error parsing response:', e);
          this.katastrskaObcinaName = '';
          return;
        }
        
        if (parsedResponse && parsedResponse.success && parsedResponse.name) {
          console.log('Setting municipality name to:', parsedResponse.name);
          this.katastrskaObcinaName = parsedResponse.name;
          // Cache the result
          this.municipalityDictionary[parsedResponse.koId] = parsedResponse.name;
          this.updateValue();
        } else {
          console.log('No name in response, clearing municipality name');
          this.katastrskaObcinaName = '';
        }
      },
      (error) => {
        console.error('Error fetching municipality name:', error);
        this.isLoadingName = false;
        this.katastrskaObcinaName = '';
      }
    );
    
    // Initialize from model if available
    if (this.value) {
      // ... existing initialization code can be added here if needed
    }
  }

  /**
   * Handle changes to Katastrska občina input
   */
  onKoChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart;
    
    // Allow only digits
    if (!/^[0-9]*$/.test(input.value)) {
      input.value = input.value.replace(/[^0-9]/g, '');
    }
    
    // Limit length
    if (input.value.length > this.maxLengthKo) {
      input.value = input.value.slice(0, this.maxLengthKo);
    }
    
    // Restore cursor position
    if (cursorPosition !== null) {
      input.setSelectionRange(cursorPosition, cursorPosition);
    }
    
    this.katastrskaObcina = input.value ? parseInt(input.value) : null;
    this.updateMunicipalityName();
    this.updateValue();
  }


  /**
   * Update the municipality name based on the code
   */
  private updateMunicipalityName() {
    if (this.katastrskaObcina && this.municipalityDictionary[this.katastrskaObcina]) {
      this.katastrskaObcinaName = this.municipalityDictionary[this.katastrskaObcina];
      this.updateValue();
    } else if (this.katastrskaObcina) {
      // Emit to subject instead of calling fetchMunicipalityName directly
      this.municipalityRequestSubject.next(this.katastrskaObcina);
    } else {
      this.katastrskaObcinaName = '';
      this.updateValue();
    }
  }


  /**
   * Update the form control value
   */
  private updateValue() {
    // The value is just the municipality name
    this.value = this.katastrskaObcinaName;
    
    // Update form control if it exists
    const attName = this.getAttribute('name');
    if (attName && this.dfms && this.dfms.globalForm && this.dfms.globalForm.controls[attName]) {
      this.dfms.globalForm.controls[attName].setValue(this.value);
      this.dfms.globalForm.controls[attName].updateValueAndValidity();
    }
    
    // Also store the municipality number in the model using dfms
    if (this.dfms && this.model && this.cadastralMunicipalityNumber) {
      this.dfms.assign(this.model, this.cadastralMunicipalityNumber, this.katastrskaObcina, true);
    }
    
    // Execute on value change script if provided
    if (this.onValueChangeEval) {
      try {
        eval(this.onValueChangeEval);
      } catch (e) {
        console.error('Error executing onValueChangeEval:', e);
      }
    }
  }

  /**
   * Check if the control has valid data
   */
  isControlValid(): boolean {
    return this.katastrskaObcina !== null && this.katastrskaObcinaName !== '';
  }

  /**
   * Clear the control
   */
  clear() {
    this.katastrskaObcina = null;
    this.katastrskaObcinaName = '';
    this.updateValue();
  }
}